import { Select, Table } from "antd";
import React, { useEffect, useState } from "react";
import { executeIntent } from "../../utils/IntentExecutor";
import IntentType from "../../../intentexecutor/utils/IntentType";
import { getContext } from "../../../intentexecutor/contextcreator/ContextCreator";
import { useGetOrgIdentifier } from "../../generic/hooks/useGetOrgIdentifier";
import { getCurrentTimeZone } from "../../../utils/dateutils";
import { performPostRequest } from "../../../service/network/NetworkService";
import ErrorNotificationComponent from "../../../components/generic/error/ErrorNotificationComponent";
import { getApiUrl } from "../../../service/network/UrlMapper";
import EditModal, { reportLogDataUpdateResponse } from "./EditModal";
import { addTotalColumnGeneric, transformData } from "./utils";
import { useObservable } from "../../generic/hooks/useObservable";

const PATIENT_REPORTING_TABLE_KEY = "patientReporting";

const PatientReporting = ({
  teams,
  months,
  loading,
  setLoading,
  wagePerHour,
  mileage,
  year,
  expandedRows,
  setExpandedRows,
  customExpandIcon,
  handleExpand,
  mapper,
  quarters,
  patientReportingData,
  setPatientReportingData,
  setServiceHour,
  setServiceRequest,
  getColumnsWithCellClick,
}) => {
  const organizationIdentifier = useGetOrgIdentifier();
  const [optionsList, setOptionsList] = useState([]);
  const [serviceIdentifiersHours, setServiceIdentifiersHours] = useState([]);
  const [serviceIdentifiersRequest, setServiceIdentifiersRequest] = useState(
    [],
  );
  const [reload, setReload] = useState(false);

  useObservable(reportLogDataUpdateResponse, setReload);

  const handleSelectServiceForHours = (value) => {
    const values = Array.isArray(value) ? value : value != null ? [value] : [];
    const services = values
      .map((v) => {
        try {
          return JSON.parse(v);
        } catch {
          return null;
        }
      })
      .filter(Boolean);
    const identifiers = services
      .map((s) => s?.serviceIdentifier)
      .filter(Boolean);
    const titles = services.map((s) => s?.title).filter(Boolean);
    setServiceHour(titles.join(", ") || "");
    setServiceIdentifiersHours(identifiers);
  };

  const handleSelectServiceForRequest = (value) => {
    const values = Array.isArray(value) ? value : value != null ? [value] : [];
    const services = values
      .map((v) => {
        try {
          return JSON.parse(v);
        } catch {
          return null;
        }
      })
      .filter(Boolean);
    const identifiers = services
      .map((s) => s?.serviceIdentifier)
      .filter(Boolean);
    const titles = services.map((s) => s?.title).filter(Boolean);
    setServiceRequest(titles.join(", ") || "");
    setServiceIdentifiersRequest(identifiers);
  };

  const getSelectedOptionValues = (identifiers) => {
    if (!identifiers?.length || !optionsList?.length) return [];
    return optionsList
      .filter((opt) => {
        try {
          const s = JSON.parse(opt.value);
          return identifiers.includes(s?.serviceIdentifier);
        } catch {
          return false;
        }
      })
      .map((opt) => opt.value);
  };

  const selectConfig = {
    totalServiceHours: {
      label: "Hours",
      handler: handleSelectServiceForHours,
    },
    totalServiceRequest: {
      label: "Requests",
      handler: handleSelectServiceForRequest,
    },
  };

  const monthlyColumns = [
    {
      title: "",
      dataIndex: "type",
      key: "type",
      width: "15%",

      render: (text, record) => {
        const config = selectConfig[record.value];

        if (config) {
          return (
            <>
              Total{" "}
              <Select
                placeholder="Click here to Select Services"
                options={optionsList}
                value={getSelectedOptionValues(
                  record.value === "totalServiceHours"
                    ? serviceIdentifiersHours
                    : serviceIdentifiersRequest,
                )}
                onChange={config.handler}
                mode="multiple"
                style={{ width: "100%" }}
                className="select-and-search-container"
              />{" "}
              {config.label}
            </>
          );
        }
        // For other rows, show plain text
        return text;
      },
    },
    ...months.map((month) => ({
      title: month,
      dataIndex: month,
      key: month,
    })),
    {
      title: "Year-to-Date (YTD)",
      dataIndex: "total",
      key: "total",
      render: (text, record) => record.total || 0,
    },
  ];

  useEffect(() => {
    const fetchServiceList = async () => {
      const payload = {
        organizationIdentifier: organizationIdentifier,
      };
      const response = await executeIntent(
        IntentType.GET_ORGANIZATION_SERVICES,
        payload,
        getContext(),
      );
      const result = response.map((service) => ({
        label: `${service.title}`,
        value: JSON.stringify(service),
      }));
      setOptionsList(result);
    };
    fetchServiceList();
  }, [organizationIdentifier]);

  useEffect(() => {
    const getReportingLogs = async () => {
      const currentTeams = teams?.map((item) => item.value);

      const payload = {
        organizationIdentifier,
        year,
        filters: ["patientReporting"],
        rate_per_hour: wagePerHour,
        miles_per_hour: mileage,
        timezone: getCurrentTimeZone(),
        teamIds: currentTeams,
        serviceIdentifiersHours,
        serviceIdentifiersRequest,
      };
      setLoading(true);
      try {
        const result = await performPostRequest(
          getApiUrl()[IntentType.GET_PATIENT_REPORTING_LOGS],
          JSON.stringify(payload),
        );
        const resultJson = await result.json();
        setPatientReportingData(resultJson);
      } catch (error) {
        ErrorNotificationComponent(error);
      } finally {
        setLoading(false);
        setReload(false);
      }
    };

    if (wagePerHour > -1 && mileage > -1) {
      getReportingLogs();
    }
  }, [
    mapper,
    mileage,
    months,
    organizationIdentifier,
    quarters,
    reload,
    serviceIdentifiersHours,
    serviceIdentifiersRequest,
    setLoading,
    setPatientReportingData,
    teams,
    wagePerHour,
    year,
  ]);

  return (
    <Table
      loading={loading}
      className="tableStyling"
      pagination={false}
      dataSource={addTotalColumnGeneric(
        patientReportingData.length
          ? transformData(patientReportingData, {
              mapper,
              months,
              quarters,
            })
          : [],
        [
          "patientDeathsOrDischarges",
          "totalNewPatientsServed",
          "totalPatientVisits",
          "totalPendingRequests",
          "vigilsPrompted",
        ],
      )}
      columns={
        getColumnsWithCellClick
          ? getColumnsWithCellClick(monthlyColumns, PATIENT_REPORTING_TABLE_KEY)
          : monthlyColumns
      }
      expandable={
        teams.length === 1 && {
          expandedRowRender: (record) => (
            <EditModal
              months={months}
              keyName={record.value}
              year={year}
              onCancel={() =>
                setExpandedRows({
                  ...expandedRows,
                  patientReporting: null,
                })
              }
              teamId={teams.length === 1 ? teams[0]?.id : ""}
            />
          ),
          expandIcon: (props) => customExpandIcon(props, "Vigils Prompted"),
          expandIconColumnIndex: monthlyColumns.length,
          onExpand: handleExpand("patientReporting"),
          expandedRowKeys: [expandedRows.patientReporting],
        }
      }
    />
  );
};

export default PatientReporting;
